<template>
  <v-row no-gutters justify="center">
    <v-col
      cols="12"
      md="5"
      class="text-center text-md-left mb-8 mb-md-0 pr-md-8"
    >
      <div class="mb-2">
        <v-chip
          color="deep-purple lighten-5"
          text-color="deep-purple"
          label
          small
          class="font-weight-bold mb-4"
        >
          👋 We'd love to hear from you
        </v-chip>
      </div>

      <h2
        class="text-h4 text-md-h3 font-weight-bold grey--text text--darken-3 mb-4 line-height-tight"
      >
        Let’s Plan Your <br />
        <span class="deep-purple--text">Perfect Celebration!</span>
      </h2>

      <p
        class="subtitle-1 grey--text text--darken-1 mb-8"
        style="max-width: 400px; margin: 0 auto 0 0"
      >
        Whether you have a specific vision or need inspiration, our team is
        ready to make it happen.
      </p>

      <v-card
        class="pa-6 d-inline-block rounded-xl"
        color="white"
        elevation="4"
      >
        <div class="d-flex align-center">
          <v-avatar color="green lighten-5" size="56" class="mr-4">
            <v-icon color="green" large>mdi-phone-in-talk</v-icon>
          </v-avatar>
          <div class="text-left">
            <div class="caption text-uppercase font-weight-bold grey--text">
              Prefer to talk to a human?
            </div>
            <a
              href="tel:+919686411733"
              class="text-h6 font-weight-bold grey--text text--darken-3 text-decoration-none hover-link"
            >
              +91 9686 411 733
            </a>
          </div>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12" md="6" lg="5">
      <v-card elevation="12" class="rounded-xl overflow-hidden">
        <v-sheet height="8" class="gradient-header"></v-sheet>

        <div class="px-8 pt-8 pb-6">
          <div class="text-h5 font-weight-bold mb-1">Request a Callback</div>
          <div class="caption grey--text mb-6">
            Fill in your details and we'll call you shortly.
          </div>

          <v-form
            ref="contactForm"
            v-model="isFormValid"
            @submit.prevent="handleSaveLead"
          >
            <v-text-field
              v-model="formData.name"
              label="Full Name"
              placeholder="John Doe"
              solo
              flat
              background-color="grey lighten-4"
              prepend-inner-icon="mdi-account-circle-outline"
              :rules="[rules.required]"
              class="rounded-lg soft-input mb-1"
              height="50"
              hide-details="auto"
            ></v-text-field>

            <v-text-field
              v-model="formData.phone"
              label="Phone Number"
              placeholder="99999 99999"
              solo
              flat
              background-color="grey lighten-4"
              type="tel"
              prepend-inner-icon="mdi-phone-outline"
              :rules="[rules.required, rules.phone]"
              class="rounded-lg soft-input mb-1 mt-4"
              height="50"
              hide-details="auto"
            ></v-text-field>

            <v-text-field
              v-model="formData.email"
              label="Email Address"
              placeholder="john@example.com"
              solo
              flat
              background-color="grey lighten-4"
              type="email"
              prepend-inner-icon="mdi-email-outline"
              :rules="[rules.required, rules.email]"
              class="rounded-lg soft-input mb-1 mt-4"
              height="50"
              hide-details="auto"
            ></v-text-field>

            <v-textarea
              v-model="formData.message"
              label="How can we help?"
              placeholder="Tell us about your event details..."
              solo
              flat
              background-color="grey lighten-4"
              rows="3"
              auto-grow
              prepend-inner-icon="mdi-message-text-outline"
              :rules="[rules.required]"
              class="rounded-lg soft-input mt-4"
              hide-details="auto"
            ></v-textarea>

            <v-btn
              block
              x-large
              type="submit"
              :loading="isLoading"
              :disabled="!isFormValid"
              :class="{ 'gradient-btn': isFormValid }"
              class="mt-8 rounded-xl white--text"
              elevation="6"
              height="56"
            >
              Send Request
              <v-icon right small>mdi-send</v-icon>
            </v-btn>

            <div class="text-center caption grey--text lighten-1 mt-4">
              <v-icon x-small color="grey lighten-1">mdi-lock</v-icon>
              Your information is safe with us.
            </div>
          </v-form>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { saveLead } from "@/services/contactus/contactusService";

export default {
  name: "ContactForm",
  data() {
    return {
      isFormValid: false,
      isLoading: false,
      formData: {
        name: "",
        phone: "",
        email: "",
        message: "",
      },
      rules: {
        required: (v) => !!v || "This field is required.",
        phone: (v) => {
          const cleanNum = v ? v.replace(/\D/g, "") : "";
          return (
            cleanNum.length === 10 || "Please enter a valid 10-digit number"
          );
        },
        email: (v) => /.+@.+\..+/.test(v) || "Please enter a valid e-mail.",
      },
    };
  },
  methods: {
    async handleSaveLead() {
      if (!this.$refs.contactForm.validate()) return;

      this.isLoading = true;

      try {
        const response = await saveLead(this.formData);

        // Success Toast
        this.$toast.success(
          response.data.message || "Request sent successfully!"
        );

        // Reset Form
        this.$refs.contactForm.reset();
      } catch (error) {
        console.error("Lead Save Error:", error);
        this.$toast.error(
          "Something went wrong. Please try calling us directly."
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.line-height-tight {
  line-height: 1.2 !important;
}

/* Custom Gradient for Button */
.gradient-btn {
  background: linear-gradient(45deg, #6200ea, #9575cd);
  transition: transform 0.2s, box-shadow 0.2s;
  letter-spacing: 1px;
  font-weight: 700;
}
.gradient-btn:active {
  transform: scale(0.98);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2) !important;
}

/* Enhances the 'Solo' field to look cleaner */
.soft-input >>> .v-input__slot {
  padding-left: 16px !important;
}

/* Link Hover Effect */
.hover-link {
  transition: color 0.2s;
}
.hover-link:hover {
  color: #6200ea !important; /* Deep Purple */
}

/* Card Hover Lift */
.v-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
</style>
